"use client"

import React, {
  ChangeEvent,
  MouseEvent,
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import TypesenseInstantSearchAdapter, {
  TypesenseInstantsearchAdapterOptions,
} from "typesense-instantsearch-adapter"
import {
  Control,
  DefaultValues,
  SubmitHandler,
  useForm,
  UseFormGetValues,
} from "react-hook-form"

import { zodResolver } from "@hookform/resolvers/zod"
import {
  downloadExportCerificatesData,
  downloadExportRequestsAsCsvMutation,
} from "@tc/ui-dfe/query"
import { toErrorMessage, toISOtime } from "@tc/ui-dfe/utils/convertor"
import { downloadFile } from "@tc/ui-dfe/utils/helper"
import { debounceQueryHook } from "@tc/ui-dfe/utils/helper/debounce"
import { Dayjs } from "@tc/ui-dfe/utils/libs/dayjs"
import { DataModel } from "@tc/ui-dfe/utils/types"
import {
  ViewAllRequestModel,
  viewAllRequestSchema,
} from "@tc/ui-dfe/utils/validations"
import useCloudWatch from "@tc/ui-rum/hooks/useCloudWatch"
import {
  UserContext,
  useTypesenseClient,
  useTypesenseTotal,
  useUiTypesenseClientProps,
} from "@tc/ui-typesense-client"
import { useClient, useMutation } from "@urql/next"
import {
  useClearRefinements,
  useCurrentRefinements,
  useHits,
  useInstantSearch,
  usePagination,
  useSearchBox,
} from "react-instantsearch"
import { useToast } from "../useToast"
import { ExportRequestType } from "@tc/graphql-server"
import { useRouter } from "next/navigation"
import {
  getExportExtractSuccessMessage,
  transformFilterState,
} from "../config/helper"

interface ViewAllRequestContextProps {
  control?: Control<ViewAllRequestModel>
  getValues?: UseFormGetValues<ViewAllRequestModel>
  clearFiltersToggle: boolean
  clearFilters: () => void
  clearRefinements: () => void
  requests: DataModel[]
  requestsCount: number
  totalRequestsCount: number
  paginationCount: number
  paginationDefaultPage: number
  onChangePagination: (event: ChangeEvent<unknown> | null, page: number) => void
  onChangeTablePagination: (
    event: MouseEvent<HTMLButtonElement> | null,
    page: number,
  ) => void
  onSortBy: (attribute: string, direction: string) => void
  typesenseApiKey?: string
  searchParams?: object
  isLoading: boolean
}
export interface ViewAllRequestProviderProps {
  children: React.ReactNode
  adapter?: TypesenseInstantSearchAdapter
  typeSenseProps: useUiTypesenseClientProps
}

export interface RequestDownloadFormValues {
  startDate: Dayjs | null
  endDate: Dayjs | null
}

export interface ViewAllRequestProps {
  onCloseDrawer?: (value?: boolean) => void
}

const ViewAllRequestContext = React.createContext<ViewAllRequestContextProps>({
  clearFiltersToggle: false,
  clearFilters: () => {},
  clearRefinements: () => {},
  requests: [],
  requestsCount: 0,
  totalRequestsCount: 0,
  onChangePagination: () => {},
  onChangeTablePagination: () => {},
  paginationCount: 0,
  paginationDefaultPage: 1,
  onSortBy: (_attribute: string, _direction: string) => {},
  typesenseApiKey: undefined,
  searchParams: undefined,
  isLoading: true,
})

const defaultValues: DefaultValues<ViewAllRequestModel> = {
  requestDestination: [],
  requestType: "all",
}

export const ViewAllRequestProvider = ({
  children,
  adapter,
  typeSenseProps,
}: ViewAllRequestProviderProps) => {
  const { control, getValues } = useForm<ViewAllRequestModel>({
    defaultValues,
    resolver: zodResolver(viewAllRequestSchema),
  })

  const [clearFiltersToggle, setClearFilterToggle] = useState(false)
  const totalFetchedRef = useRef(false)

  const { status } = useInstantSearch()
  const isLoading = status === "loading" || status === "stalled"

  const { hits } = useHits({})
  const [requestData, setRequestData] = useState<DataModel[]>([])
  const { nbHits, nbPages, refine, currentRefinement } = usePagination({})

  const typesenseTotalProps = useMemo(
    () => ({
      searchParams: totalFetchedRef.current
        ? undefined
        : typeSenseProps.searchParams,
      collectionId: typeSenseProps.collectionId,
      skipFetch: totalFetchedRef.current,
      context: typeSenseProps.context ?? ("exports" as UserContext),
    }),
    [
      typeSenseProps.searchParams,
      typeSenseProps.collectionId,
      typeSenseProps.context,
    ],
  )

  const { totalResults } = useTypesenseTotal(typesenseTotalProps)

  useEffect(() => {
    if (totalResults > 0) {
      totalFetchedRef.current = true
    }
  }, [totalResults])

  useEffect(() => {
    const data: DataModel[] = hits.map((hit) => {
      return {
        id: hit["id"] as string,
        ...hit,
      }
    })

    setRequestData(data)
  }, [hits])

  const { adapterProps, apiKey: typesenseApiKey } =
    useTypesenseClient(typeSenseProps)
  const clearFilters = useClearRefinements({})
  const onPageChange = (pageIndex: number) => {
    refine(pageIndex - 1)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  const searchBox = useSearchBox({ queryHook: debounceQueryHook })

  const clearFiltersCallback = useCallback(() => {
    setClearFilterToggle(!clearFiltersToggle)
    clearFilters.refine()
  }, [clearFiltersToggle, clearFilters])

  const clearRefinementsCallback = useCallback(
    () => clearFilters.refine(),
    [clearFilters],
  )

  const onChangePaginationCallback = useCallback(
    (_event: ChangeEvent<unknown> | null, page: number) => onPageChange(page),
    [refine],
  )

  const onChangeTablePaginationCallback = useCallback(
    (_event: MouseEvent<HTMLButtonElement> | null, page: number) =>
      onPageChange(page),
    [refine],
  )

  const onSortByCallback = useCallback(
    (attribute: string, direction: string) => {
      if (adapterProps) {
        const props: TypesenseInstantsearchAdapterOptions = {
          ...adapterProps,
          additionalSearchParameters: {
            ...adapterProps.additionalSearchParameters,
            sort_by: `${attribute}:${direction}`,
          },
        }
        adapter?.updateConfiguration(props)
        searchBox.refine(searchBox.query)
      }
    },
    [adapterProps, adapter, searchBox],
  )

  const contextValue = useMemo(
    () => ({
      control,
      getValues,
      clearFiltersToggle,
      clearFilters: clearFiltersCallback,
      clearRefinements: clearRefinementsCallback,
      requests: requestData,
      requestsCount: nbHits,
      totalRequestsCount: totalResults,
      paginationCount: nbPages,
      paginationDefaultPage: currentRefinement + 1,
      onChangePagination: onChangePaginationCallback,
      onChangeTablePagination: onChangeTablePaginationCallback,
      typesenseApiKey: typesenseApiKey,
      searchParams: typeSenseProps.searchParams,
      onSortBy: onSortByCallback,
      isLoading,
    }),
    [
      control,
      getValues,
      clearFiltersToggle,
      clearFiltersCallback,
      clearRefinementsCallback,
      requestData,
      nbHits,
      totalResults,
      nbPages,
      currentRefinement,
      onChangePaginationCallback,
      onChangeTablePaginationCallback,
      typesenseApiKey,
      typeSenseProps.searchParams,
      onSortByCallback,
      isLoading,
    ],
  )

  return (
    <Suspense fallback={<div></div>}>
      <ViewAllRequestContext.Provider value={contextValue}>
        {children}
      </ViewAllRequestContext.Provider>
    </Suspense>
  )
}

export const useViewAllRequest = (props?: ViewAllRequestProps) => {
  const requestContext = useContext(ViewAllRequestContext)
  const { onCloseDrawer } = props || {}
  const { query: queryText } = useSearchBox({})
  const client = useClient()
  const { logError } = useCloudWatch()
  const { items: appliedFilters } = useCurrentRefinements()
  const [, downloadCsv] = useMutation(
    downloadExportRequestsAsCsvMutation.toString(),
  )
  const router = useRouter()

  const toast = useToast()
  const { control, handleSubmit, reset } = useForm<RequestDownloadFormValues>({
    defaultValues: {
      startDate: null,
      endDate: null,
    },
  })
  const clearAll = () => {
    reset({
      startDate: null,
      endDate: null,
    })
  }

  const getDownloadData = async (
    startDate: Dayjs | null,
    endDate: Dayjs | null,
  ) => {
    const result = await client.query(
      downloadExportCerificatesData.toString(),
      {
        startDate: startDate ? toISOtime(startDate.startOf("day")) : startDate,
        endDate: endDate ? toISOtime(endDate.endOf("day")) : endDate,
      },
      {
        requestPolicy: "network-only",
      },
    )
    const { data, error } = result
    if (error) {
      throw error
    }
    return data
  }

  const submitHander: SubmitHandler<RequestDownloadFormValues> = async (
    data,
  ) => {
    try {
      const filedata = await getDownloadData(data?.startDate, data?.endDate)
      if (filedata) {
        downloadFile(
          filedata?.downloadExportCertificates?.url,
          filedata?.downloadExportCertificates?.filename,
        )
      }
    } catch (error) {
      logError("useViewAllRequest request download submit", error)
      if ((error as Error).message.includes("[GraphQL]")) {
        toast.error("Failed", toErrorMessage((error as Error).message))
      }
    } finally {
      onCloseDrawer?.()
      clearAll()
    }
  }

  const exportDataDownloadHandler = async (
    exportRequestType: ExportRequestType,
  ) => {
    const downloadKey = "download-progress"
    try {
      toast.info(
        "Downloading",
        "It might take a moment, please wait",
        false,
        undefined,
        downloadKey,
      )

      const filter = transformFilterState(appliedFilters)
      const pageSearchParams =
        (requestContext.searchParams as Record<string, string>) || {}
      const result = await downloadCsv(
        {
          input: {
            filter,
            exportRequestType,
            query: queryText || "*",
            queryBy: pageSearchParams.query_by,
            infix: pageSearchParams.infix,
            queryByWeights: pageSearchParams.query_by_weights,
          },
        },
        {
          fetchOptions: {
            headers: {
              "X-TYPESENSE-API-KEY": requestContext.typesenseApiKey || "",
            },
          },
        },
      )

      if (result.data?.downloadExportRequestsAsCsv) {
        downloadFile(
          result.data.downloadExportRequestsAsCsv.presignedUrl,
          result.data.downloadExportRequestsAsCsv.filename,
          false,
        )

        // Dismiss info toast and show success toast
        toast.dismiss(downloadKey)
        const successMessage = getExportExtractSuccessMessage(exportRequestType)
        toast.success("Successfully downloaded", successMessage)
      } else if (result.error?.networkError) {
        logError(
          "useViewAllRequest csv download - connection timed out",
          result.error.networkError,
        )
        toast.dismiss(downloadKey)
        router.push("/connection-timeout")
      } else throw new Error(result.error?.message)
    } catch (error) {
      logError("useViewAllRequest csv download", error)
      toast.dismiss(downloadKey)
      if (error as Error) {
        toast.error("Export failed", toErrorMessage((error as Error).message))
      }
    }
  }

  const onSubmit = handleSubmit(submitHander)
  return {
    ...requestContext,
    control,
    clearAll,
    onSubmit,
    exportDataDownloadHandler,
  }
}
